import './common.scss';

import { Box, ColumnLayout, SelectProps, SpaceBetween, TableProps } from '@amzn/awsui-components-react';

import { constants } from '../../../common/Localization';
import { formatCurrencyNoDecimals, formatNumber, formatNumberAsString } from '../../../common/LocalizationHelper';
import AWSCATTable from '../../../common/table/Table-index';
import { ColumnDefinition, ColumnTypes } from '../../../common/table/types';
import { roundToInteger } from '../../../common/util';
import { getCell } from '../../common/awsui-table-helper';
import { CashFlowData, CommonResourceDetailTableItem, CommonSummaryItem } from './types';
export const summaryColumnDefs = (currency: string): TableProps.ColumnDefinition<CommonSummaryItem>[] => [{
    id: 'type',
    header: constants.TCO_PAGE_SUMMARY_TYPE,
    cell: (item: CommonSummaryItem) => (<Box float='left'>{item.type}</Box>),
}, {
    id: 'description',
    header: constants.TCO_PAGE_SUMMARY_DESCRIPTION,
    cell: (item: CommonSummaryItem) => (<Box float='left'>{item.description}</Box>),
}, {
    id: 'cost',
    header: <Box fontWeight='bold' float='right'>{constants.TCO_PAGE_SUMMARY_COST}</Box>,
    cell: (item: CommonSummaryItem) => (
        <Box float='right'>
            {item.cost != null ? formatCurrencyNoDecimals(item.cost, currency) : '-'}
        </Box>
    ),
}];

export const commonTotalCostSummary = (
    currency: string,
    totalCost: number,
    adjustment: number,
    adjustedTotal: number,
    adjustmentDesc?: string,
): JSX.Element => {
    return (
        <ColumnLayout disableGutters columns={2} variant='default' className='totals-grid'>
            <Box variant='h3' margin={{vertical: 'xs'}} padding='n'><strong>{constants.TCO_TOTAL_COST}</strong></Box>
            <Box variant='h3' margin={{vertical: 'xs'}} padding='n' float='right'><strong>
                {formatCurrencyNoDecimals(totalCost, currency)}
            </strong></Box>
            <Box variant='h3' margin={{vertical: 'xs'}} padding='n'>
                {constants.ADJUSTMENT + (adjustmentDesc ? ' (' + adjustmentDesc + ')' : '')}
            </Box>
            <Box variant='h3' margin={{vertical: 'xs'}} padding='n' float='right'>
                {formatCurrencyNoDecimals(adjustment, currency)}
            </Box>
            <Box variant='h3' margin={{vertical: 'xs'}} padding='n'><strong>{constants.TCO_ADJUSTED_TOTAL}</strong></Box>
            <Box variant='h3' margin={{vertical: 'xs'}} padding='n' float='right'><strong>
                {formatCurrencyNoDecimals(adjustedTotal, currency)}
            </strong></Box>
        </ColumnLayout>
    );
};

const generateCashFlowColumns = (numYear: number, currency: string): ColumnDefinition<CashFlowData>[] => {
    const columnDefs: ColumnDefinition<CashFlowData>[] = [{
        id: 'type',
        header: constants.TCO_PAGE_SUMMARY_TYPE,
        cell: item => getCell(item.type, item.highlight, 'left'),
        type: ColumnTypes.TEXT,
        text:  constants.TCO_PAGE_SUMMARY_TYPE,
    }];
    for (let yearIndex = 0; yearIndex < numYear; yearIndex++) {
        columnDefs.push({
            id: 'year' + yearIndex,
            header: `${constants.YEAR} ${yearIndex + 1}`,
            data: (item: CashFlowData) => roundToInteger(item['year' + yearIndex]),
            cell: item => item.otherUnit ?
                getCell(formatNumberAsString(item['year' + yearIndex], {maximumFractionDigits: 0}) + ` ${item.otherUnit}`, item.highlight, 'right') :
                getCell(formatCurrencyNoDecimals(item['year' + yearIndex], currency), item.highlight, 'right'),
            type: ColumnTypes.NUMBER,
            text: `${constants.YEAR} ${yearIndex + 1}`,
        });
    }
    return columnDefs;
};

export const generateCashFlowTable = (cashFlowData: CashFlowData[], numYear: number, currency: string, tableName?: string): JSX.Element => {
    return (
        <AWSCATTable
            resourceName={tableName ?? constants.CASH_FLOW}
            items={cashFlowData}
            columnDefinitions={generateCashFlowColumns(numYear, currency)}
            useDefaultDownload
            defaultPageSize={25}
            autoGenerateVisibleContent
        />
    );
};

/**
 * Defines the column definitions for common resource details table
 */

export const commonResourceDetailColumns = (currency: string): ColumnDefinition<CommonResourceDetailTableItem>[] => [{
    id: 'resource',
    header: constants.RESOURCE,
    cell: item => item.resource,
    type: ColumnTypes.TEXT,
    text: constants.RESOURCE,
}, {
    id: 'unit',
    header: constants.UNIT,
    data: item => item.unit ?? '',
    cell: item => getCell(
        typeof(item.unit) === 'number' ? formatNumber(item.unit, { maximumFractionDigits: 0 }) : (item.unit ?? '')),
    type: ColumnTypes.NUMBER,
    text: constants.UNIT,
}, {
    id: 'totalCost',
    header: constants.TCO_TOTAL_COST,
    data: item => item.totalCost ?? '',
    cell: item => getCell(
        item.totalCost ? formatCurrencyNoDecimals(item.totalCost, currency) : ''),
    type: ColumnTypes.NUMBER,
    text: constants.TCO_TOTAL_COST,
}];

export const overrideFieldOptions: SelectProps.Options = [
    { label: constants.AVERAGE_UTILIZATION, value: 'AvgUtilization' },
    { label: constants.PEAK_UTILIZATION, value: 'PeakUtilization' },
    { label: constants.ON_PREM_CAPACITY, value: 'OnPremCapacity' },
];

export const buildStorageAndNetworkSummary = (
    total: number,
    storageManualAdj: number,
    networkManualAdj: number,
    adjustedTotal: number,
    currency: string,
    storageManualAdjDescription?: string,
    networkManualAdjDescription?: string,
): JSX.Element => {
    return (
        <SpaceBetween size='m'>
            <ColumnLayout disableGutters columns={2} variant='default' className='storage-network-total-grid'>
                <Box variant='h3' ><strong>{constants.TCO_TOTAL_COST}</strong></Box>
                <Box variant='h3' float='right'><strong>
                    {formatCurrencyNoDecimals(total, currency)}
                </strong></Box>
                <Box variant='h3'>{constants.STORAGE_ADJUSTMENT + (storageManualAdjDescription ? ' (' + storageManualAdjDescription + ')' : '')}</Box>
                <Box variant='h3' float='right'>
                    {formatCurrencyNoDecimals(storageManualAdj, currency)}
                </Box>
                <Box variant='h3'>{constants.NETWORK_ADJUSTMENT + (networkManualAdjDescription ? ' (' + networkManualAdjDescription + ')' : '')}</Box>
                <Box variant='h3' float='right'>
                    {formatCurrencyNoDecimals(networkManualAdj, currency)}
                </Box>
                <Box variant='h3'><strong>{constants.TCO_ADJUSTED_TOTAL}</strong></Box>
                <Box variant='h3' float='right'><strong>
                    {formatCurrencyNoDecimals(adjustedTotal, currency)}
                </strong></Box>
            </ColumnLayout>
        </SpaceBetween>
    );
};

export const buildITLaborAndSupportSummary = (
    total: number,
    itLaborManualAdj: number,
    supportManualAdj: number,
    adjustedTotal: number,
    currency: string,
): JSX.Element => {
    return (
        <SpaceBetween size='m'>
            <ColumnLayout disableGutters columns={2} variant='default' className='storage-network-total-grid'>
                <Box variant='h3' ><strong>{constants.TCO_TOTAL_COST}</strong></Box>
                <Box variant='h3' float='right'><strong>
                    {formatCurrencyNoDecimals(total, currency)}
                </strong></Box>
                <Box variant='h3'>{constants.IT_LABOR_ADJUSTMENT}</Box>
                <Box variant='h3' float='right'>
                    {formatCurrencyNoDecimals(itLaborManualAdj, currency)}
                </Box>
                <Box variant='h3'>{constants.SUPPORT_ADJUSTMENT}</Box>
                <Box variant='h3' float='right'>
                    {formatCurrencyNoDecimals(supportManualAdj, currency)}
                </Box>
                <Box variant='h3'><strong>{constants.TCO_ADJUSTED_TOTAL}</strong></Box>
                <Box variant='h3' float='right'><strong>
                    {formatCurrencyNoDecimals(adjustedTotal, currency)}
                </strong></Box>
            </ColumnLayout>
        </SpaceBetween>
    );
};
